import type { ChangeEvent, ReactNode } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import { orderBy } from 'lodash';
import type { Address } from 'viem';

import Spinner from '@uikit/components/Spinner/Spinner';

import type { OneInchNetwork, OneInchToken } from '@shared/api/types/magicSwap';

import { useGetOneInchNetworksQuery, useGetOneInchTokensQuery } from '@query/magicSwap';

import { TokenOption } from '@magic-swap/components/pages/MagicSwap/ui/TokenOption';
import { Select, SelectOption } from '@ui/uikit/components/common/Select';
import { TokenSymbol } from '@ui/uikit/components/common/TokenSymbol';
import { ArrowDown } from '@ui/uikit/components/icons/mono/ArrowDown';
import { ArrowLeft } from '@ui/uikit/components/icons/mono/ArrowLeft';
import { ArrowRight } from '@ui/uikit/components/icons/mono/ArrowRight';

import { useWizardContext } from '../components/wizard/Wizard';

interface SwapTokenSelectorProps {
  label?: string;
  network?: OneInchNetwork;
  token?: OneInchToken;
  onChange?: (value: SwapTokenSelectorPromptData) => void;
  className?: string;
}

interface EnhancedOneInchToken extends OneInchToken {
  handleSelect: () => void;
}

interface NetworkSelectIcon {
  url?: string;
}

const NetworkSelectIcon = ({ url }: NetworkSelectIcon) => {
  return url ? (
    <div
      className="size-[32px] bg-contain bg-no-repeat bg-center"
      style={{ backgroundImage: `url("${url}")` }}
    ></div>
  ) : null;
};

interface FavoriteTokenOptionsProps {
  tokens: Array<EnhancedOneInchToken>;
}

const FavoriteTokenOptions = ({ tokens }: FavoriteTokenOptionsProps) => {
  const favoriteTokens = useMemo(() => {
    return tokens.filter(({ attributes }) => attributes.favorite);
  }, [tokens]);

  return (
    <div className="flex gap-[8px] flex-wrap my-4">
      {favoriteTokens.map((token) => (
        <TokenOption
          key={token.id}
          hideName
          selectedView
          token={token}
          className="px-[8px] py-[4px] text-[14px] text-neutral-600 rounded-[8px] border border-1 border-base-border-medium cursor-pointer hover:bg-primary-100 hover:border-base-border-dark transition-colors"
          onClick={token.handleSelect}
        />
      ))}
    </div>
  );
};

interface TokensListProps {
  tokens: Array<EnhancedOneInchToken>;
  onSelect?(tokenAddress: Address): void;
}

const TokensList = ({ tokens }: TokensListProps) => {
  return (
    <div className="relative min-h-0">
      <div className="h-full overflow-y-scroll">
        <div className="my-[16px]">
          {tokens.map(({ id, attributes: { logoURI, name, symbol }, handleSelect }) => (
            <div
              onClick={handleSelect}
              className="group flex flex-row items-center justify-between py-[8px] border-t border-primary-100 first:border-none cursor-pointer"
              key={id}
            >
              <TokenSymbol
                icon={logoURI}
                ticker={symbol}
                note={name}
                appearance={{
                  caption: 'text-neutral-600',
                }}
              />
              <ArrowRight className="icon-xs mr-[8px] text-neutral-600 opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all" />
            </div>
          ))}
        </div>
      </div>

      <div className="absolute top-0 w-full h-[16px] bg-gradient-to-b from-[#FFF] to-transparent"></div>
      <div className="absolute bottom-0 w-full h-[16px] bg-gradient-to-t from-[#FFF] to-transparent"></div>
    </div>
  );
};

interface SwapTokenNetworkSelectProps {
  value?: number;
  networks?: Array<OneInchNetwork>;
  isDisabled?: boolean;
  onValueChange?: (networkId: number) => void;
}

const SwapTokenNetworkSelect = ({
  value,
  networks = [],
  isDisabled,
  onValueChange,
}: SwapTokenNetworkSelectProps) => {
  const preprocessedNetworks = useMemo(() => {
    return networks.filter((network) => !!network.attributes.name);
  }, [networks]);

  const handleNetworkSelectValueChange = useCallback(
    (value: string) => {
      const numericNetworkId = Number.parseFloat(value);

      onValueChange?.(numericNetworkId);
    },
    [onValueChange],
  );

  return (
    <Select
      value={value?.toString()}
      disabled={isDisabled}
      onValueChange={handleNetworkSelectValueChange}
    >
      {preprocessedNetworks.map(({ id, attributes: { name, logoUrl } }) => {
        return (
          <SelectOption
            value={id.toString()}
            Icon={<NetworkSelectIcon url={logoUrl} />}
            className="flex flex-row gap-[8px] z-30"
            key={id}
          >
            {name}
          </SelectOption>
        );
      })}
    </Select>
  );
};

interface SwapTokenSelectorControlGroupProps {
  networkId: number;
  onSelect?(tokenAddress: Address): void;
}

const SwapTokenSelectorControlGroup = ({
  networkId,
  onSelect,
}: SwapTokenSelectorControlGroupProps) => {
  const { data: tokens = [], isFetching: isTokensQueryPending } = useGetOneInchTokensQuery({
    networkId,
    options: {
      placeholderData: (prev) => prev,
    },
  });

  const orderedTokens = useMemo(() => {
    return orderBy(tokens, ({ attributes: { name } }) => name);
  }, [tokens]);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleSearchInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const enhancedTokens = useMemo(() => {
    return orderedTokens.map<EnhancedOneInchToken>(({ id, ...token }) => {
      return {
        id,
        ...token,
        handleSelect: () => {
          onSelect?.(id);
        },
      };
    });
  }, [orderedTokens, onSelect]);

  const filteredTokens = useMemo(() => {
    const regExp = new RegExp(searchTerm, 'i');

    return searchTerm
      ? enhancedTokens.filter((token) => {
          return regExp.test(token.attributes.name) || regExp.test(token.attributes.symbol);
        })
      : enhancedTokens;
  }, [enhancedTokens, searchTerm]);

  return (
    <div className="relative flex flex-col gap-[16px] min-h-0 grow">
      <div
        className={classNames('transition-opacity flex flex-col gap-[16px] h-full', {
          'opacity-50': isTokensQueryPending,
        })}
      >
        <input
          name="search-tokens"
          type="search"
          value={searchTerm}
          onChange={handleSearchInputChange}
          className="msq-input msq-search-input pl-[2.25rem] peer w-full hover:border-base-border-dark focus-within:border-base-border-dark"
          placeholder="Search tokens..."
        />

        <FavoriteTokenOptions tokens={filteredTokens} />

        <TokensList tokens={filteredTokens} />
      </div>

      <div
        className={classNames(
          'absolute size-full flex flex-col items-center justify-center content-center',
          { hidden: !isTokensQueryPending },
        )}
      >
        <Spinner size="sm" color="primary" />
        <p className="text-neutral-400 font-bold">Loading tokens...</p>
      </div>
    </div>
  );
};

interface SwapTokenSelectorPromptData {
  network: OneInchNetwork;
  token: OneInchToken;
}

interface SwapTokenSelectorOverlayProps {
  network?: OneInchNetwork;
  header?: ReactNode;
  onComplete?(data?: SwapTokenSelectorPromptData): void;
}

const SwapTokenSelectorPrompt = ({
  network,
  header,
  onComplete,
}: SwapTokenSelectorOverlayProps) => {
  const { data: networks = [], isPending } = useGetOneInchNetworksQuery();

  const [networkId, setNetworkId] = useState<number | undefined>(network?.id);

  const { data: tokens = [] } = useGetOneInchTokensQuery({
    networkId,
    options: { enabled: false },
  });

  const handleSelect = useCallback(
    (tokenAddress: Address) => {
      console.log('SwapTokenSelectorPrompt::handleSelect');

      if (onComplete) {
        const network = networks.find(({ id }) => id === networkId);

        if (!network) {
          console.warn(`Network id ${networkId} not found.`);

          return;
        }

        const normalizedTokenAddress = tokenAddress.toLowerCase();

        const token = tokens.find(({ id }) => id.toLowerCase() === normalizedTokenAddress);

        if (!token) {
          console.warn(`Token with address ${tokenAddress} not found.`);

          return;
        }

        onComplete({
          network,
          token,
        });
      }
    },
    [networks, networkId, tokens, onComplete],
  );

  const handleNavigateBackButtonClick = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  return (
    <div className="bg-neutral-50">
      <div className="p-[32px] w-full h-full">
        <div className="flex flex-col gap-[32px] h-full overflow-y-hidden">
          <div className="flex flex-row gap-[6px] items-center font-[600] text-[28px]">
            <ArrowLeft
              className="size-[32px] cursor-pointer"
              onClick={handleNavigateBackButtonClick}
            />
            {header}
          </div>

          <div className="flex flex-col gap-[16px] w-full h-full min-h-0">
            <SwapTokenNetworkSelect
              value={networkId}
              isDisabled={isPending}
              networks={networks}
              onValueChange={setNetworkId}
            />

            {networkId && (
              <SwapTokenSelectorControlGroup networkId={networkId} onSelect={handleSelect} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SwapTokenSelector = ({
  network,
  token,
  label,
  className,
  onChange,
}: SwapTokenSelectorProps) => {
  const { overlayContainer } = useWizardContext();

  const [isOverlayVisible, setIsOverlayVisible] = useState<boolean>(false);

  const handleClick = useCallback(() => {
    if (!overlayContainer) {
      console.warn('Wizard context seems to be unavailable.');

      return;
    }

    setIsOverlayVisible(true);
  }, [overlayContainer]);

  const handleOverlayComplete = useCallback(
    (value: SwapTokenSelectorPromptData) => {
      console.log('SwapTokenSelector::handleOverlayComplete');
      onChange?.(value);
      setIsOverlayVisible(false);
    },
    [onChange, setIsOverlayVisible],
  );

  return (
    <Fragment>
      <div
        className={classNames('flex flex-row items-center gap-[4px] cursor-pointer', className)}
        onClick={handleClick}
      >
        <TokenSymbol
          icon={
            token?.attributes.logoURI || (
              <div className="skeleton-block skeleton-pulse size-[32px] rounded-[50%]" />
            )
          }
          ticker={token?.attributes.symbol || 'Select a token'}
          note={network?.attributes.name}
        />

        <ArrowDown className="text-neutral-600 size-[16px]" />
      </div>

      {isOverlayVisible &&
        overlayContainer &&
        createPortal(
          <SwapTokenSelectorPrompt
            header={label}
            network={network}
            onComplete={handleOverlayComplete}
          />,
          overlayContainer,
        )}
    </Fragment>
  );
};

export { SwapTokenSelector, type SwapTokenSelectorPromptData };
